import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import Seo from "@/components/site/Seo";
import NotFound from "@/pages/NotFound";
import ConversionCTA from "@/components/site/ConversionCTA";
import { api } from "@/lib/brands";

// Shared Tailwind styling for CMS rich-text so the live page and the editor
// preview look identical.
export const PROSE =
  "max-w-3xl text-sm leading-relaxed text-muted " +
  "[&_h2]:mt-8 [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:font-medium [&_h2]:text-ink " +
  "[&_h3]:mt-6 [&_h3]:font-serif [&_h3]:text-xl [&_h3]:font-medium [&_h3]:text-ink " +
  "[&_p]:mt-3 [&_a]:text-accent [&_a]:underline " +
  "[&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 " +
  "[&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-5 " +
  "[&_strong]:text-ink [&_img]:mt-4 [&_img]:max-w-full [&_img]:border [&_img]:border-hair";

export default function DynamicPage({ slug: fixedSlug }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const [page, setPage] = useState(undefined); // undefined=loading, null=not found

  useEffect(() => {
    let active = true;
    setPage(undefined);
    api
      .get(`/pages/${slug}`)
      .then(({ data }) => active && setPage(data))
      .catch(() => active && setPage(null));
    return () => {
      active = false;
    };
  }, [slug]);

  if (page === undefined) {
    return <div className="py-24 text-center font-mono text-sm text-muted" data-testid="page-loading">Loading…</div>;
  }
  if (page === null) return <NotFound />;

  const clean = DOMPurify.sanitize(page.content || "", { USE_PROFILES: { html: true } });

  return (
    <div className="pb-16" data-testid={`page-${slug}`}>
      <Seo title={page.meta_title || page.title} description={page.meta_description} />
      <section className="border-b border-hair py-12 sm:py-16">
        {page.eyebrow && (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{page.eyebrow}</p>
        )}
        <h1 className="mt-3 max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-ink sm:text-5xl">
          {page.title}
        </h1>
      </section>
      <article className={`mt-10 ${PROSE}`} data-testid="page-content" dangerouslySetInnerHTML={{ __html: clean }} />
      <ConversionCTA />
    </div>
  );
}
